import React, { useState, useEffect } from 'react';
import {
    Paper,
    Typography,
    Box,
    Button,
    Modal,
    useTheme,
    useMediaQuery,
    ToggleButtonGroup,
    ToggleButton,
    FormControlLabel,
    Checkbox,
    Tabs,
    Tab,
    Collapse,
    Select,
    MenuItem,
    FormControl
} from '@mui/material';
import {
    BarChart as BarChartIcon
} from '@mui/icons-material';
import {
    ComposedChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    Line,
    ReferenceLine
} from 'recharts';
import { inkColors } from './constants/inkColors';

const LimitedTierlistStats = ({ cards }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [displayMode, setDisplayMode] = useState('sum'); // 'sum', 'average', 'count', 'weighted'
    const [excludeDualInks, setExcludeDualInks] = useState(false);
    const [activeTab, setActiveTab] = useState('colors'); // 'colors', 'costs'
    const [costView, setCostView] = useState('colors'); // 'colors', 'rarity' - tylko dla activeTab === 'costs'
    const [showControls, setShowControls] = useState(false);
    const [visibleColors, setVisibleColors] = useState({
        AMBER: true,
        AMETHYST: true,
        EMERALD: true,
        RUBY: true,
        SAPPHIRE: true,
        STEEL: true
    });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Detekcja Safari na iOS
    const isSafari = () => {
        const userAgent = navigator.userAgent;
        return /iPad|iPhone|iPod/.test(userAgent) && /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
    };

    const isIOSSafari = isSafari();

    // Wymuszenie przerenderowania wykresu gdy zmienia się wysokość
    useEffect(() => {
        const timer = setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 100);
        return () => clearTimeout(timer);
    }, [showControls]);

    // Szacunkowa liczba kart danej rzadkości w boosterze
    const rarityBoosterRates = {
        COMMON: 6.65,
        UNCOMMON: 3.17,
        RARE: 1.63,
        SUPER: 0.63,
        LEGENDARY: 0.19,
        SPECIAL: 0.1 // Karty specjalne są bardzo rzadkie
    };

    // Oblicz statystyki per kolor i rzadkość
    const getColorRarityStats = () => {
        const colorRarityStats = {};
        const rarities = ['COMMON', 'UNCOMMON', 'RARE', 'SUPER', 'LEGENDARY', 'SPECIAL'];

        // Oblicz liczbę kart danego rarity w każdym secie
        const setRarityCounts = {};
        cards.forEach(card => {
            if (card.set && card.set.name && card.rarity) {
                if (!setRarityCounts[card.set.name]) {
                    setRarityCounts[card.set.name] = {};
                }
                if (!setRarityCounts[card.set.name][card.rarity]) {
                    setRarityCounts[card.set.name][card.rarity] = 0;
                }
                setRarityCounts[card.set.name][card.rarity]++;
            }
        });

        // Inicjalizuj strukturę dla każdego koloru
        Object.keys(inkColors).forEach(color => {
            colorRarityStats[color] = {};
            rarities.forEach(rarity => {
                colorRarityStats[color][rarity] = {
                    totalRating: 0,
                    cardCount: 0,
                    cardsWithRating2Plus: 0,
                    totalWeightedRating: 0
                };
            });
            // Dodaj pola dla sum per kolor
            colorRarityStats[color].totalRating = 0;
            colorRarityStats[color].totalCardCount = 0;
            colorRarityStats[color].totalCardsWithRating2Plus = 0;
            colorRarityStats[color].totalWeightedRating = 0;
        });

        // Zbierz dane
        cards.forEach(card => {
            if (card.magic_ink_colors && card.rating && card.rarity && card.set) {
                // Sprawdź czy karta ma tylko jeden kolor atramentu (jeśli checkbox jest zaznaczony)
                if (excludeDualInks && card.magic_ink_colors.length > 1) {
                    return; // Pomiń karty z wieloma kolorami
                }

                card.magic_ink_colors.forEach(color => {
                    if (colorRarityStats[color] && colorRarityStats[color][card.rarity]) {
                        colorRarityStats[color][card.rarity].totalRating += card.rating;
                        colorRarityStats[color][card.rarity].cardCount += 1;
                        if (card.rating >= 2) {
                            colorRarityStats[color][card.rarity].cardsWithRating2Plus += 1;
                        }

                        // Oblicz ważoną ocenę: ocena * szacunkowa_liczba_kart_w_boosterze * (1 / liczba_kart_danego_rarity_w_secie)
                        const cardsInSetOfRarity = setRarityCounts[card.set.name]?.[card.rarity] || 1;
                        const dropChance = rarityBoosterRates[card.rarity] * (1 / cardsInSetOfRarity);
                        const weightedRating = card.rating * dropChance;
                        colorRarityStats[color][card.rarity].totalWeightedRating += weightedRating;

                        // Dodaj do sum per kolor
                        colorRarityStats[color].totalRating += card.rating;
                        colorRarityStats[color].totalCardCount += 1;
                        if (card.rating >= 2) {
                            colorRarityStats[color].totalCardsWithRating2Plus += 1;
                        }
                        colorRarityStats[color].totalWeightedRating += weightedRating;
                    }
                });
            }
        });

        // Konwertuj na format dla wykresu z ręczną kolejnością
        const result = [];
        Object.keys(colorRarityStats).forEach(color => {
            const colorData = {
                color: inkColors[color]?.name || color,
                colorKey: color,
                COMMON: displayMode === 'sum'
                    ? colorRarityStats[color].COMMON.totalRating
                    : displayMode === 'average'
                        ? colorRarityStats[color].COMMON.cardCount > 0
                            ? Math.round((colorRarityStats[color].COMMON.totalRating / colorRarityStats[color].COMMON.cardCount) * 10) / 10
                            : 0
                        : displayMode === 'weighted'
                            ? Math.round(colorRarityStats[color].COMMON.totalWeightedRating * 100) / 100
                            : colorRarityStats[color].COMMON.cardsWithRating2Plus,
                UNCOMMON: displayMode === 'sum'
                    ? colorRarityStats[color].UNCOMMON.totalRating
                    : displayMode === 'average'
                        ? colorRarityStats[color].UNCOMMON.cardCount > 0
                            ? Math.round((colorRarityStats[color].UNCOMMON.totalRating / colorRarityStats[color].UNCOMMON.cardCount) * 10) / 10
                            : 0
                        : displayMode === 'weighted'
                            ? Math.round(colorRarityStats[color].UNCOMMON.totalWeightedRating * 100) / 100
                            : colorRarityStats[color].UNCOMMON.cardsWithRating2Plus,
                RARE: displayMode === 'sum'
                    ? colorRarityStats[color].RARE.totalRating
                    : displayMode === 'average'
                        ? colorRarityStats[color].RARE.cardCount > 0
                            ? Math.round((colorRarityStats[color].RARE.totalRating / colorRarityStats[color].RARE.cardCount) * 10) / 10
                            : 0
                        : displayMode === 'weighted'
                            ? Math.round(colorRarityStats[color].RARE.totalWeightedRating * 100) / 100
                            : colorRarityStats[color].RARE.cardsWithRating2Plus,
                SUPER: displayMode === 'sum'
                    ? colorRarityStats[color].SUPER.totalRating
                    : displayMode === 'average'
                        ? colorRarityStats[color].SUPER.cardCount > 0
                            ? Math.round((colorRarityStats[color].SUPER.totalRating / colorRarityStats[color].SUPER.cardCount) * 10) / 10
                            : 0
                        : displayMode === 'weighted'
                            ? Math.round(colorRarityStats[color].SUPER.totalWeightedRating * 100) / 100
                            : colorRarityStats[color].SUPER.cardsWithRating2Plus,
                LEGENDARY: displayMode === 'sum'
                    ? colorRarityStats[color].LEGENDARY.totalRating
                    : displayMode === 'average'
                        ? colorRarityStats[color].LEGENDARY.cardCount > 0
                            ? Math.round((colorRarityStats[color].LEGENDARY.totalRating / colorRarityStats[color].LEGENDARY.cardCount) * 10) / 10
                            : 0
                        : displayMode === 'weighted'
                            ? Math.round(colorRarityStats[color].LEGENDARY.totalWeightedRating * 100) / 100
                            : colorRarityStats[color].LEGENDARY.cardsWithRating2Plus,
                total: displayMode === 'sum'
                    ? colorRarityStats[color].totalRating
                    : displayMode === 'average'
                        ? colorRarityStats[color].totalCardCount > 0
                            ? Math.round((colorRarityStats[color].totalRating / colorRarityStats[color].totalCardCount) * 10) / 10
                            : 0
                        : displayMode === 'weighted'
                            ? Math.round(colorRarityStats[color].totalWeightedRating * 100) / 100
                            : colorRarityStats[color].totalCardsWithRating2Plus
            };
            result.push(colorData);
        });

        return result;
    };

    // Oblicz statystyki per koszt mana
    const getCostRarityStats = () => {
        if (costView === 'colors') {
            // Widok per kolory atramentu
            const costColorStats = {};
            const colors = ['AMBER', 'AMETHYST', 'EMERALD', 'RUBY', 'SAPPHIRE', 'STEEL'];

            // Oblicz liczbę kart danego rarity w każdym secie
            const setRarityCounts = {};
            cards.forEach(card => {
                if (card.set && card.set.name && card.rarity) {
                    if (!setRarityCounts[card.set.name]) {
                        setRarityCounts[card.set.name] = {};
                    }
                    if (!setRarityCounts[card.set.name][card.rarity]) {
                        setRarityCounts[card.set.name][card.rarity] = 0;
                    }
                    setRarityCounts[card.set.name][card.rarity]++;
                }
            });

            // Inicjalizuj strukturę dla każdego kosztu mana (bez 0, 8+ jako jedna kategoria)
            for (let cost = 1; cost <= 7; cost++) {
                costColorStats[cost] = {};
                colors.forEach(color => {
                    costColorStats[cost][color] = {
                        totalRating: 0,
                        cardCount: 0,
                        cardsWithRating2Plus: 0,
                        totalWeightedRating: 0
                    };
                });
                // Dodaj pola dla sum per koszt
                costColorStats[cost].totalRating = 0;
                costColorStats[cost].totalCardCount = 0;
                costColorStats[cost].totalCardsWithRating2Plus = 0;
                costColorStats[cost].totalWeightedRating = 0;
            }
            // Dodaj kategorię 8+
            costColorStats['8+'] = {};
            colors.forEach(color => {
                costColorStats['8+'][color] = {
                    totalRating: 0,
                    cardCount: 0,
                    cardsWithRating2Plus: 0,
                    totalWeightedRating: 0
                };
            });
            costColorStats['8+'].totalRating = 0;
            costColorStats['8+'].totalCardCount = 0;
            costColorStats['8+'].totalCardsWithRating2Plus = 0;
            costColorStats['8+'].totalWeightedRating = 0;

            // Zbierz dane
            cards.forEach(card => {
                if (card.ink_cost !== undefined && card.rating && card.rarity && card.set && card.magic_ink_colors) {
                    let cost = card.ink_cost;
                    // Grupuj koszty 8+ w jedną kategorię
                    if (cost >= 8) {
                        cost = '8+';
                    }
                    // Pomijaj koszt 0
                    if (cost === 0) {
                        return;
                    }
                    if (costColorStats[cost]) {
                        // Sprawdź kolory atramentu karty
                        card.magic_ink_colors.forEach(color => {
                            if (costColorStats[cost][color]) {
                                costColorStats[cost][color].totalRating += card.rating;
                                costColorStats[cost][color].cardCount += 1;
                                if (card.rating >= 2) {
                                    costColorStats[cost][color].cardsWithRating2Plus += 1;
                                }

                                // Oblicz ważoną ocenę: ocena * szacunkowa_liczba_kart_w_boosterze * (1 / liczba_kart_danego_rarity_w_secie)
                                const cardsInSetOfRarity = setRarityCounts[card.set.name]?.[card.rarity] || 1;
                                const dropChance = rarityBoosterRates[card.rarity] * (1 / cardsInSetOfRarity);
                                const weightedRating = card.rating * dropChance;
                                costColorStats[cost][color].totalWeightedRating += weightedRating;

                                // Dodaj do sum per koszt
                                costColorStats[cost].totalRating += card.rating;
                                costColorStats[cost].totalCardCount += 1;
                                if (card.rating >= 2) {
                                    costColorStats[cost].totalCardsWithRating2Plus += 1;
                                }
                                costColorStats[cost].totalWeightedRating += weightedRating;
                            }
                        });
                    }
                }
            });

            // Konwertuj na format dla wykresu
            const result = [];
            Object.keys(costColorStats).forEach(cost => {
                const costData = {
                    cost: `${cost}`,
                    costKey: cost,
                    AMBER: displayMode === 'sum'
                        ? costColorStats[cost].AMBER.totalRating
                        : displayMode === 'average'
                            ? costColorStats[cost].AMBER.cardCount > 0
                                ? Math.round((costColorStats[cost].AMBER.totalRating / costColorStats[cost].AMBER.cardCount) * 10) / 10
                                : 0
                            : displayMode === 'weighted'
                                ? Math.round(costColorStats[cost].AMBER.totalWeightedRating * 100) / 100
                                : costColorStats[cost].AMBER.cardsWithRating2Plus,
                    AMETHYST: displayMode === 'sum'
                        ? costColorStats[cost].AMETHYST.totalRating
                        : displayMode === 'average'
                            ? costColorStats[cost].AMETHYST.cardCount > 0
                                ? Math.round((costColorStats[cost].AMETHYST.totalRating / costColorStats[cost].AMETHYST.cardCount) * 10) / 10
                                : 0
                            : displayMode === 'weighted'
                                ? Math.round(costColorStats[cost].AMETHYST.totalWeightedRating * 100) / 100
                                : costColorStats[cost].AMETHYST.cardsWithRating2Plus,
                    EMERALD: displayMode === 'sum'
                        ? costColorStats[cost].EMERALD.totalRating
                        : displayMode === 'average'
                            ? costColorStats[cost].EMERALD.cardCount > 0
                                ? Math.round((costColorStats[cost].EMERALD.totalRating / costColorStats[cost].EMERALD.cardCount) * 10) / 10
                                : 0
                            : displayMode === 'weighted'
                                ? Math.round(costColorStats[cost].EMERALD.totalWeightedRating * 100) / 100
                                : costColorStats[cost].EMERALD.cardsWithRating2Plus,
                    RUBY: displayMode === 'sum'
                        ? costColorStats[cost].RUBY.totalRating
                        : displayMode === 'average'
                            ? costColorStats[cost].RUBY.cardCount > 0
                                ? Math.round((costColorStats[cost].RUBY.totalRating / costColorStats[cost].RUBY.cardCount) * 10) / 10
                                : 0
                            : displayMode === 'weighted'
                                ? Math.round(costColorStats[cost].RUBY.totalWeightedRating * 100) / 100
                                : costColorStats[cost].RUBY.cardsWithRating2Plus,
                    SAPPHIRE: displayMode === 'sum'
                        ? costColorStats[cost].SAPPHIRE.totalRating
                        : displayMode === 'average'
                            ? costColorStats[cost].SAPPHIRE.cardCount > 0
                                ? Math.round((costColorStats[cost].SAPPHIRE.totalRating / costColorStats[cost].SAPPHIRE.cardCount) * 10) / 10
                                : 0
                            : displayMode === 'weighted'
                                ? Math.round(costColorStats[cost].SAPPHIRE.totalWeightedRating * 100) / 100
                                : costColorStats[cost].SAPPHIRE.cardsWithRating2Plus,
                    STEEL: displayMode === 'sum'
                        ? costColorStats[cost].STEEL.totalRating
                        : displayMode === 'average'
                            ? costColorStats[cost].STEEL.cardCount > 0
                                ? Math.round((costColorStats[cost].STEEL.totalRating / costColorStats[cost].STEEL.cardCount) * 10) / 10
                                : 0
                            : displayMode === 'weighted'
                                ? Math.round(costColorStats[cost].STEEL.totalWeightedRating * 100) / 100
                                : costColorStats[cost].STEEL.cardsWithRating2Plus,
                    total: displayMode === 'sum'
                        ? costColorStats[cost].totalRating
                        : displayMode === 'average'
                            ? costColorStats[cost].totalCardCount > 0
                                ? Math.round((costColorStats[cost].totalRating / costColorStats[cost].totalCardCount) * 10) / 10
                                : 0
                            : displayMode === 'weighted'
                                ? Math.round(costColorStats[cost].totalWeightedRating * 100) / 100
                                : costColorStats[cost].totalCardsWithRating2Plus
                };
                result.push(costData);
            });

            return result;
        } else {
            // Widok per rarity
            const costRarityStats = {};
            const rarities = ['COMMON', 'UNCOMMON', 'RARE', 'SUPER', 'LEGENDARY', 'SPECIAL'];

            // Oblicz liczbę kart danego rarity w każdym secie
            const setRarityCounts = {};
            cards.forEach(card => {
                if (card.set && card.set.name && card.rarity) {
                    if (!setRarityCounts[card.set.name]) {
                        setRarityCounts[card.set.name] = {};
                    }
                    if (!setRarityCounts[card.set.name][card.rarity]) {
                        setRarityCounts[card.set.name][card.rarity] = 0;
                    }
                    setRarityCounts[card.set.name][card.rarity]++;
                }
            });

            // Inicjalizuj strukturę dla każdego kosztu mana (bez 0, 8+ jako jedna kategoria)
            for (let cost = 1; cost <= 7; cost++) {
                costRarityStats[cost] = {};
                rarities.forEach(rarity => {
                    costRarityStats[cost][rarity] = {
                        totalRating: 0,
                        cardCount: 0,
                        cardsWithRating2Plus: 0,
                        totalWeightedRating: 0
                    };
                });
                // Dodaj pola dla sum per koszt
                costRarityStats[cost].totalRating = 0;
                costRarityStats[cost].totalCardCount = 0;
                costRarityStats[cost].totalCardsWithRating2Plus = 0;
                costRarityStats[cost].totalWeightedRating = 0;
            }
            // Dodaj kategorię 8+
            costRarityStats['8+'] = {};
            rarities.forEach(rarity => {
                costRarityStats['8+'][rarity] = {
                    totalRating: 0,
                    cardCount: 0,
                    cardsWithRating2Plus: 0,
                    totalWeightedRating: 0
                };
            });
            costRarityStats['8+'].totalRating = 0;
            costRarityStats['8+'].totalCardCount = 0;
            costRarityStats['8+'].totalCardsWithRating2Plus = 0;
            costRarityStats['8+'].totalWeightedRating = 0;

            // Zbierz dane
            cards.forEach(card => {
                if (card.ink_cost !== undefined && card.rating && card.rarity && card.set) {
                    let cost = card.ink_cost;
                    // Grupuj koszty 8+ w jedną kategorię
                    if (cost >= 8) {
                        cost = '8+';
                    }
                    // Pomijaj koszt 0
                    if (cost === 0) {
                        return;
                    }
                    if (costRarityStats[cost] && costRarityStats[cost][card.rarity]) {
                        costRarityStats[cost][card.rarity].totalRating += card.rating;
                        costRarityStats[cost][card.rarity].cardCount += 1;
                        if (card.rating >= 2) {
                            costRarityStats[cost][card.rarity].cardsWithRating2Plus += 1;
                        }

                        // Oblicz ważoną ocenę: ocena * szacunkowa_liczba_kart_w_boosterze * (1 / liczba_kart_danego_rarity_w_secie)
                        const cardsInSetOfRarity = setRarityCounts[card.set.name]?.[card.rarity] || 1;
                        const dropChance = rarityBoosterRates[card.rarity] * (1 / cardsInSetOfRarity);
                        const weightedRating = card.rating * dropChance;
                        costRarityStats[cost][card.rarity].totalWeightedRating += weightedRating;

                        // Dodaj do sum per koszt
                        costRarityStats[cost].totalRating += card.rating;
                        costRarityStats[cost].totalCardCount += 1;
                        if (card.rating >= 2) {
                            costRarityStats[cost].totalCardsWithRating2Plus += 1;
                        }
                        costRarityStats[cost].totalWeightedRating += weightedRating;
                    }
                }
            });

            // Konwertuj na format dla wykresu
            const result = [];
            Object.keys(costRarityStats).forEach(cost => {
                const costData = {
                    cost: `${cost}`,
                    costKey: cost,
                    COMMON: displayMode === 'sum'
                        ? costRarityStats[cost].COMMON.totalRating
                        : displayMode === 'average'
                            ? costRarityStats[cost].COMMON.cardCount > 0
                                ? Math.round((costRarityStats[cost].COMMON.totalRating / costRarityStats[cost].COMMON.cardCount) * 10) / 10
                                : 0
                            : displayMode === 'weighted'
                                ? Math.round(costRarityStats[cost].COMMON.totalWeightedRating * 100) / 100
                                : costRarityStats[cost].COMMON.cardsWithRating2Plus,
                    UNCOMMON: displayMode === 'sum'
                        ? costRarityStats[cost].UNCOMMON.totalRating
                        : displayMode === 'average'
                            ? costRarityStats[cost].UNCOMMON.cardCount > 0
                                ? Math.round((costRarityStats[cost].UNCOMMON.totalRating / costRarityStats[cost].UNCOMMON.cardCount) * 10) / 10
                                : 0
                            : displayMode === 'weighted'
                                ? Math.round(costRarityStats[cost].UNCOMMON.totalWeightedRating * 100) / 100
                                : costRarityStats[cost].UNCOMMON.cardsWithRating2Plus,
                    RARE: displayMode === 'sum'
                        ? costRarityStats[cost].RARE.totalRating
                        : displayMode === 'average'
                            ? costRarityStats[cost].RARE.cardCount > 0
                                ? Math.round((costRarityStats[cost].RARE.totalRating / costRarityStats[cost].RARE.cardCount) * 10) / 10
                                : 0
                            : displayMode === 'weighted'
                                ? Math.round(costRarityStats[cost].RARE.totalWeightedRating * 100) / 100
                                : costRarityStats[cost].RARE.cardsWithRating2Plus,
                    SUPER: displayMode === 'sum'
                        ? costRarityStats[cost].SUPER.totalRating
                        : displayMode === 'average'
                            ? costRarityStats[cost].SUPER.cardCount > 0
                                ? Math.round((costRarityStats[cost].SUPER.totalRating / costRarityStats[cost].SUPER.cardCount) * 10) / 10
                                : 0
                            : displayMode === 'weighted'
                                ? Math.round(costRarityStats[cost].SUPER.totalWeightedRating * 100) / 100
                                : costRarityStats[cost].SUPER.cardsWithRating2Plus,
                    LEGENDARY: displayMode === 'sum'
                        ? costRarityStats[cost].LEGENDARY.totalRating
                        : displayMode === 'average'
                            ? costRarityStats[cost].LEGENDARY.cardCount > 0
                                ? Math.round((costRarityStats[cost].LEGENDARY.totalRating / costRarityStats[cost].LEGENDARY.cardCount) * 10) / 10
                                : 0
                            : displayMode === 'weighted'
                                ? Math.round(costRarityStats[cost].LEGENDARY.totalWeightedRating * 100) / 100
                                : costRarityStats[cost].LEGENDARY.cardsWithRating2Plus,
                    total: displayMode === 'sum'
                        ? costRarityStats[cost].totalRating
                        : displayMode === 'average'
                            ? costRarityStats[cost].totalCardCount > 0
                                ? Math.round((costRarityStats[cost].totalRating / costRarityStats[cost].totalCardCount) * 10) / 10
                                : 0
                            : displayMode === 'weighted'
                                ? Math.round(costRarityStats[cost].totalWeightedRating * 100) / 100
                                : costRarityStats[cost].totalCardsWithRating2Plus
                };
                result.push(costData);
            });

            return result;
        }
    };

    const colorRarityStats = getColorRarityStats();
    const costRarityStats = getCostRarityStats();

    // Oblicz liczbę kart po filtrowaniu
    const getFilteredCardCount = () => {
        if (!excludeDualInks) return cards.length;
        return cards.filter(card => card.magic_ink_colors && card.magic_ink_colors.length === 1).length;
    };

    const handleOpenModal = () => {
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleDisplayModeChange = (event, newMode) => {
        if (newMode !== null) {
            setDisplayMode(newMode);
        }
    };

    const handleExcludeDualInksChange = (event) => {
        setExcludeDualInks(event.target.checked);
    };

    const handleColorVisibilityChange = (color) => {
        setVisibleColors(prev => ({
            ...prev,
            [color]: !prev[color]
        }));
    };

    // Kolory dla rzadkości - dopasowane do chipów z tabeli tierlist
    const rarityBarColors = {
        COMMON: '#6b7280',
        UNCOMMON: '#ffffff',
        RARE: '#d2691e',
        SUPER: '#c0c0c0',
        LEGENDARY: '#daa520'
    };

    // Gradienty dla rzadkości - dopasowane do chipów z tabeli tierlist
    const rarityBarGradients = {
        COMMON: 'url(#commonGradient)',
        UNCOMMON: 'url(#uncommonGradient)',
        RARE: 'url(#rareGradient)',
        SUPER: 'url(#superGradient)',
        LEGENDARY: 'url(#legendaryGradient)'
    };

    // Kolory dla kolorów atramentu
    const inkBarColors = {
        AMBER: '#f59e0b',
        AMETHYST: '#8b5cf6',
        EMERALD: '#10b981',
        RUBY: '#ef4444',
        SAPPHIRE: '#3b82f6',
        STEEL: '#6b7280'
    };

    // Opis wykresu w zależności od trybu wyświetlania
    const getChartDescription = () => {
        const filterInfo = excludeDualInks ? ' (tylko karty z pojedynczym kolorem atramentu)' : '';

        switch (displayMode) {
            case 'sum':
                return isMobile
                    ? `Wykres przedstawia sumę ocen kart dla każdego koloru atramentu z podziałem na rzadkości${filterInfo}`
                    : `Wykres przedstawia sumę ocen kart dla każdego koloru atramentu z podziałem na rzadkości (Common, Uncommon, Rare, Super, Legendary, Special)${filterInfo}`;
            case 'average':
                return isMobile
                    ? `Wykres przedstawia średnią ocen kart dla każdego koloru atramentu z podziałem na rzadkości${filterInfo}`
                    : `Wykres przedstawia średnią ocen kart dla każdego koloru atramentu z podziałem na rzadkości (Common, Uncommon, Rare, Super, Legendary, Special)${filterInfo}`;
            case 'count':
                return isMobile
                    ? `Wykres przedstawia liczbę kart z oceną ≥2 dla każdego koloru atramentu z podziałem na rzadkości${filterInfo}`
                    : `Wykres przedstawia liczbę kart z oceną ≥2 dla każdego koloru atramentu z podziałem na rzadkości (Common, Uncommon, Rare, Super, Legendary, Special)${filterInfo}`;
            case 'weighted':
                return isMobile
                    ? `Wykres przedstawia xScore kart uwzględniający szanse na drop${filterInfo}`
                    : `Wykres przedstawia xScore kart uwzględniający szanse na drop (ocena × szanse w boosterze × 1/liczba kart danego rarity w secie)${filterInfo}`;
            default:
                return '';
        }
    };

    return (
        <>
            {/* Przycisk otwierający modal */}
            <Paper
                elevation={0}
                sx={{
                    backgroundColor: 'rgba(17, 24, 39, 0.8)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(76, 29, 149, 0.3)',
                    borderRadius: '16px',
                    p: 3,
                    mb: 3,
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: 'rgba(17, 24, 39, 0.9)',
                        border: '1px solid rgba(76, 29, 149, 0.5)',
                        transition: 'all 0.2s ease'
                    }
                }}
                onClick={handleOpenModal}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                    <BarChartIcon sx={{ color: 'white', fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        Statystyki Kart ({getFilteredCardCount()} kart{excludeDualInks ? ' - tylko pojedyncze kolory' : ''})
                    </Typography>
                </Box>
            </Paper>

            {/* Modal ze statystykami */}
            <Modal
                open={modalOpen}
                onClose={handleCloseModal}
                aria-labelledby="stats-modal-title"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: isIOSSafari ? { xs: 0.5, sm: 1 } : { xs: 1, sm: 2 }
                }}
            >
                <Paper
                    elevation={24}
                    sx={{
                        backgroundColor: 'rgba(17, 24, 39, 0.95)',
                        backdropFilter: 'blur(20px)',
                        border: '2px solid rgba(76, 29, 149, 0.5)',
                        borderRadius: '20px',
                        width: isIOSSafari
                            ? { xs: '92vw', sm: '90vw', md: '85vw' }
                            : { xs: '98vw', sm: '95vw', md: '90vw' },
                        height: isIOSSafari
                            ? { xs: '92vh', sm: '90vh', md: '85vh' }
                            : { xs: '98vh', sm: '95vh', md: '90vh' },
                        maxWidth: { xs: 'none', md: '1200px' },
                        maxHeight: { xs: 'none', md: '90vh' },
                        overflow: 'hidden',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {/* Nagłówek modala - kompaktowy i nowoczesny */}
                    <Box sx={{
                        p: isIOSSafari
                            ? { xs: 1.5, sm: 2, md: 2.5 }
                            : { xs: 2, sm: 2.5, md: 3 },
                        borderBottom: '1px solid rgba(76, 29, 149, 0.3)',
                        flexShrink: 0
                    }}>
                        {/* Górny rząd: Taby (desktop) / Przyciski (mobile) + Kontrolki */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1.5
                        }}>
                            {/* Taby na desktop, przyciski na mobile */}
                            {!isMobile ? (
                                <Tabs
                                    value={activeTab}
                                    onChange={(event, newValue) => setActiveTab(newValue)}
                                    sx={{
                                        '& .MuiTab-root': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            fontWeight: 600,
                                            fontSize: '1rem',
                                            textTransform: 'none',
                                            minWidth: '120px',
                                            '&.Mui-selected': {
                                                color: 'white'
                                            }
                                        },
                                        '& .MuiTabs-indicator': {
                                            backgroundColor: 'rgba(76, 29, 149, 0.8)',
                                            height: '3px'
                                        }
                                    }}
                                >
                                    <Tab label="Podział na kolory" value="colors" />
                                    <Tab label="Podział na koszt" value="costs" />
                                </Tabs>
                            ) : (
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <Select
                                        value={activeTab}
                                        onChange={(event) => setActiveTab(event.target.value)}
                                        sx={{
                                            color: 'white',
                                            backgroundColor: 'rgba(17, 24, 39, 0.3)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            borderRadius: '6px',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            '& .MuiSelect-icon': {
                                                color: 'rgba(255, 255, 255, 0.7)'
                                            },
                                            '&:hover': {
                                                border: '1px solid rgba(255, 255, 255, 0.4)',
                                                backgroundColor: 'rgba(17, 24, 39, 0.4)'
                                            },
                                            '&.Mui-focused': {
                                                border: '1px solid rgba(76, 29, 149, 0.5)',
                                                backgroundColor: 'rgba(17, 24, 39, 0.5)'
                                            }
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: 'rgba(17, 24, 39, 0.95)',
                                                    border: '1px solid rgba(76, 29, 149, 0.3)',
                                                    borderRadius: '8px',
                                                    mt: 0.5
                                                }
                                            }
                                        }}
                                    >
                                        <MenuItem value="colors" sx={{
                                            color: 'white',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            '&:hover': {
                                                backgroundColor: 'rgba(76, 29, 149, 0.2)'
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: 'rgba(76, 29, 149, 0.3)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(76, 29, 149, 0.4)'
                                                }
                                            }
                                        }}>
                                            Podział na kolory
                                        </MenuItem>
                                        <MenuItem value="costs" sx={{
                                            color: 'white',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            '&:hover': {
                                                backgroundColor: 'rgba(76, 29, 149, 0.2)'
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: 'rgba(76, 29, 149, 0.3)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(76, 29, 149, 0.4)'
                                                }
                                            }
                                        }}>
                                            Per koszt
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            )}

                            {/* Kontrolki w prawej części */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}>
                                {/* Przycisk ustawień */}
                                <Button
                                    onClick={() => setShowControls(!showControls)}
                                    size="small"
                                    sx={{
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        minWidth: 'auto',
                                        px: isMobile ? 1 : 1.5,
                                        py: 0.5,
                                        fontSize: isMobile ? '0.7rem' : '0.75rem',
                                        fontWeight: 500,
                                        borderRadius: '6px',
                                        backgroundColor: 'rgba(17, 24, 39, 0.3)',
                                        '&:hover': {
                                            border: '1px solid rgba(255, 255, 255, 0.4)',
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                            color: 'white'
                                        }
                                    }}
                                >
                                    {isMobile ? (showControls ? 'Ukryj' : 'Ustaw') : (showControls ? 'Ukryj' : 'Pokaż')} {isMobile ? '' : 'ustawienia'}
                                </Button>

                                {/* Przycisk zamknięcia */}
                                <Button
                                    onClick={handleCloseModal}
                                    size="small"
                                    sx={{
                                        color: 'rgba(255, 255, 255, 0.8)',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        minWidth: isMobile ? '50px' : '60px',
                                        px: isMobile ? 1 : 1.5,
                                        py: 0.5,
                                        fontSize: isMobile ? '0.7rem' : '0.75rem',
                                        fontWeight: 500,
                                        borderRadius: '6px',
                                        backgroundColor: 'rgba(17, 24, 39, 0.3)',
                                        '&:hover': {
                                            border: '1px solid rgba(255, 255, 255, 0.4)',
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                            color: 'white'
                                        }
                                    }}
                                >
                                    {isMobile ? 'X' : 'Zamknij'}
                                </Button>
                            </Box>
                        </Box>



                        {/* Chowalne kontrolki */}
                        <Collapse in={showControls}>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                alignItems: isMobile ? 'stretch' : 'center',
                                gap: isMobile ? 1.5 : 2,
                                flexWrap: 'wrap',
                                pt: 1,
                                borderTop: '1px solid rgba(76, 29, 149, 0.2)'
                            }}>
                                {/* Tryb wyświetlania */}
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: isMobile ? 'column' : 'row',
                                    alignItems: isMobile ? 'stretch' : 'center',
                                    gap: 1
                                }}>
                                    <Typography sx={{
                                        color: 'rgba(255, 255, 255, 0.6)',
                                        fontSize: isMobile ? '0.7rem' : '0.75rem',
                                        fontWeight: 500,
                                        textAlign: isMobile ? 'center' : 'left'
                                    }}>
                                        Tryb wyświetlania
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={displayMode}
                                        exclusive
                                        onChange={handleDisplayModeChange}
                                        size="small"
                                        sx={{
                                            '& .MuiToggleButton-root': {
                                                color: 'rgba(255, 255, 255, 0.7)',
                                                border: 'none',
                                                px: isMobile ? 0.75 : 1,
                                                py: isMobile ? 0.5 : 0.25,
                                                fontSize: isMobile ? '0.65rem' : '0.7rem',
                                                fontWeight: 600,
                                                borderRadius: '4px',
                                                minWidth: isMobile ? '45px' : '50px',
                                                '&.Mui-selected': {
                                                    backgroundColor: 'rgba(76, 29, 149, 0.7)',
                                                    color: 'white'
                                                },
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                                    color: 'white'
                                                }
                                            }
                                        }}
                                    >
                                        <ToggleButton value="sum">{isMobile ? 'Suma' : 'Suma'}</ToggleButton>
                                        <ToggleButton value="average">{isMobile ? 'Średnia' : 'Średnia'}</ToggleButton>
                                        <ToggleButton value="count">{isMobile ? 'Ocena ≥2' : 'Ocena ≥2'}</ToggleButton>
                                        <ToggleButton value="weighted">{isMobile ? 'xScore' : 'xScore'}</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>

                                {/* Checkbox dual inków */}
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={excludeDualInks}
                                            onChange={handleExcludeDualInksChange}
                                            size="small"
                                            sx={{
                                                color: 'rgba(255, 255, 255, 0.4)',
                                                '&.Mui-checked': {
                                                    color: 'rgba(76, 29, 149, 0.8)'
                                                }
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{
                                            color: 'rgba(255, 255, 255, 0.6)',
                                            fontSize: isMobile ? '0.7rem' : '0.75rem',
                                            fontWeight: 500,
                                            textAlign: isMobile ? 'center' : 'left'
                                        }}>
                                            {isMobile ? 'Tylko pojedyncze kolory' : 'Tylko pojedyncze kolory'}
                                        </Typography>
                                    }
                                    sx={{
                                        m: 0,
                                        justifyContent: isMobile ? 'center' : 'flex-start'
                                    }}
                                />

                                {/* Kontrolki dla taba "Per koszt" */}
                                {activeTab === 'costs' && (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: isMobile ? 'column' : 'row',
                                        alignItems: isMobile ? 'stretch' : 'center',
                                        gap: 1
                                    }}>
                                        {/* Wybór widoku */}
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: isMobile ? 'column' : 'row',
                                            alignItems: isMobile ? 'stretch' : 'center',
                                            gap: 1
                                        }}>
                                            <Typography sx={{
                                                color: 'rgba(255, 255, 255, 0.6)',
                                                fontSize: isMobile ? '0.7rem' : '0.75rem',
                                                fontWeight: 500,
                                                textAlign: isMobile ? 'center' : 'left'
                                            }}>
                                                Widok:
                                            </Typography>
                                            <ToggleButtonGroup
                                                value={costView}
                                                exclusive
                                                onChange={(event, newValue) => newValue && setCostView(newValue)}
                                                size="small"
                                                sx={{
                                                    '& .MuiToggleButton-root': {
                                                        color: 'rgba(255, 255, 255, 0.7)',
                                                        border: 'none',
                                                        px: isMobile ? 0.75 : 1,
                                                        py: isMobile ? 0.5 : 0.25,
                                                        fontSize: isMobile ? '0.65rem' : '0.7rem',
                                                        fontWeight: 600,
                                                        borderRadius: '4px',
                                                        minWidth: isMobile ? '45px' : '50px',
                                                        '&.Mui-selected': {
                                                            backgroundColor: 'rgba(76, 29, 149, 0.7)',
                                                            color: 'white'
                                                        },
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                                            color: 'white'
                                                        }
                                                    }
                                                }}
                                            >
                                                <ToggleButton value="colors">{isMobile ? 'Kolory' : 'Kolory'}</ToggleButton>
                                                <ToggleButton value="rarity">{isMobile ? 'Rarity' : 'Rarity'}</ToggleButton>
                                            </ToggleButtonGroup>
                                        </Box>

                                        {/* Kontrolki kolorów - tylko gdy widok kolory */}
                                        {costView === 'colors' && (
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: isMobile ? 'column' : 'row',
                                                alignItems: isMobile ? 'stretch' : 'center',
                                                gap: 1
                                            }}>
                                                <Typography sx={{
                                                    color: 'rgba(255, 255, 255, 0.6)',
                                                    fontSize: isMobile ? '0.7rem' : '0.75rem',
                                                    fontWeight: 500,
                                                    textAlign: isMobile ? 'center' : 'left'
                                                }}>
                                                    Widoczne kolory
                                                </Typography>
                                                <Box sx={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 0.5,
                                                    justifyContent: isMobile ? 'center' : 'flex-start'
                                                }}>
                                                    {Object.entries(visibleColors).map(([color, isVisible]) => (
                                                        <Box
                                                            key={color}
                                                            onClick={() => handleColorVisibilityChange(color)}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.5,
                                                                cursor: 'pointer',
                                                                px: isMobile ? 0.5 : 0.75,
                                                                py: isMobile ? 0.5 : 0.25,
                                                                borderRadius: '4px',
                                                                backgroundColor: isVisible ? 'rgba(76, 29, 149, 0.3)' : 'transparent',
                                                                border: `1px solid ${isVisible ? 'rgba(76, 29, 149, 0.5)' : 'transparent'}`,
                                                                transition: 'all 0.2s ease',
                                                                '&:hover': {
                                                                    backgroundColor: isVisible ? 'rgba(76, 29, 149, 0.4)' : 'rgba(255, 255, 255, 0.05)'
                                                                }
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: isMobile ? 12 : 10,
                                                                    height: isMobile ? 12 : 10,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: inkBarColors[color],
                                                                    opacity: isVisible ? 1 : 0.4
                                                                }}
                                                            />
                                                            <Typography sx={{
                                                                color: isVisible ? 'white' : 'rgba(255, 255, 255, 0.5)',
                                                                fontSize: isMobile ? '0.7rem' : '0.65rem',
                                                                fontWeight: 600,
                                                                textTransform: 'capitalize'
                                                            }}>
                                                                {isMobile ? color.charAt(0) + color.slice(1).toLowerCase() : color.charAt(0) + color.slice(1).toLowerCase()}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Collapse>
                    </Box>



                    {/* Zawartość modala */}
                    <Box sx={{
                        p: isIOSSafari
                            ? { xs: 1, sm: 1.5, md: 3 }
                            : { xs: 1.5, sm: 2, md: 4 },
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}>
                        {/* Wykres */}
                        <Box sx={{
                            flex: 1,
                            width: '100%',
                            minHeight: { xs: '200px', sm: '250px', md: '300px' },
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden'
                        }}>
                            <ResponsiveContainer width="100%" height="100%" key={`${activeTab}-${showControls}`}>
                                <ComposedChart
                                    data={activeTab === 'colors' ? colorRarityStats : costRarityStats}
                                    margin={{
                                        top: 10,
                                        right: isMobile ? 15 : 25,
                                        bottom: isMobile ? 25 : 35,
                                        left: isMobile ? 15 : 25
                                    }}
                                    layout={isMobile ? "vertical" : "horizontal"}
                                >
                                    <defs>
                                        {/* Gradienty dla rzadkości */}
                                        <linearGradient id="commonGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#6b7280" />
                                            <stop offset="100%" stopColor="#4b5563" />
                                        </linearGradient>
                                        <linearGradient id="uncommonGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#ffffff" />
                                            <stop offset="100%" stopColor="#e5e5e5" />
                                        </linearGradient>
                                        <linearGradient id="rareGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#d2691e" />
                                            <stop offset="100%" stopColor="#b8860b" />
                                        </linearGradient>
                                        <linearGradient id="superGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#a8a8a8" />
                                            <stop offset="100%" stopColor="#c0c0c0" />
                                        </linearGradient>
                                        <linearGradient id="legendaryGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#8b6914" />
                                            <stop offset="100%" stopColor="#daa520" />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke="rgba(255,255,255,0.1)"
                                        horizontal={!isMobile}
                                        vertical={isMobile}
                                    />

                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: 'rgba(17, 24, 39, 0.95)',
                                            border: '1px solid rgba(76, 29, 149, 0.3)',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '12px'
                                        }}
                                    />

                                    {isMobile ? (
                                        // Wykres pionowy dla telefonów
                                        <>
                                            <XAxis
                                                type="number"
                                                stroke="rgba(255,255,255,0.7)"
                                                fontSize={12}
                                                xAxisId={0}
                                            />
                                            <XAxis
                                                type="number"
                                                orientation="top"
                                                stroke="#ff6b6b"
                                                fontSize={10}
                                                tick={{ fill: "#ff6b6b" }}
                                                xAxisId={1}
                                                domain={[0, 'dataMax']}
                                            />
                                            <YAxis
                                                type="category"
                                                dataKey={activeTab === 'colors' ? "color" : "cost"}
                                                stroke="rgba(255,255,255,0.7)"
                                                fontSize={12}
                                                width={80}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="total"
                                                stroke="#ff6b6b"
                                                strokeWidth={3}
                                                dot={{ fill: "#ff6b6b", strokeWidth: 2, r: 4 }}
                                                name="Suma"
                                                connectNulls={false}
                                                xAxisId={1}
                                            />
                                            {activeTab === 'colors' ? (
                                                // Słupki dla rzadkości (tab "Podział na kolory")
                                                <>
                                                    <Bar
                                                        dataKey="COMMON"
                                                        fill={rarityBarGradients.COMMON}
                                                        radius={[0, 4, 4, 0]}
                                                        name="Common"
                                                        stroke="#4b5563"
                                                        strokeWidth={1}
                                                        xAxisId={0}
                                                    />
                                                    <Bar
                                                        dataKey="UNCOMMON"
                                                        fill={rarityBarGradients.UNCOMMON}
                                                        radius={[0, 4, 4, 0]}
                                                        name="Uncommon"
                                                        stroke="#e5e5e5"
                                                        strokeWidth={1}
                                                        xAxisId={0}
                                                    />
                                                    <Bar
                                                        dataKey="RARE"
                                                        fill={rarityBarGradients.RARE}
                                                        radius={[0, 4, 4, 0]}
                                                        name="Rare"
                                                        stroke="#b8860b"
                                                        strokeWidth={1}
                                                        xAxisId={0}
                                                    />
                                                    <Bar
                                                        dataKey="SUPER"
                                                        fill={rarityBarGradients.SUPER}
                                                        radius={[0, 4, 4, 0]}
                                                        name="Super"
                                                        stroke="#e5e5e5"
                                                        strokeWidth={1}
                                                        xAxisId={0}
                                                    />
                                                    <Bar
                                                        dataKey="LEGENDARY"
                                                        fill={rarityBarGradients.LEGENDARY}
                                                        radius={[0, 4, 4, 0]}
                                                        name="Legendary"
                                                        stroke="#daa520"
                                                        strokeWidth={1}
                                                        xAxisId={0}
                                                    />
                                                </>
                                            ) : (
                                                // Słupki dla taba "Per koszt"
                                                <>
                                                    {costView === 'colors' ? (
                                                        // Słupki dla kolorów
                                                        <>
                                                            {Object.entries(visibleColors).map(([color, isVisible]) => isVisible && (
                                                                <Bar
                                                                    key={color}
                                                                    dataKey={color}
                                                                    fill={inkBarColors[color]}
                                                                    radius={[0, 4, 4, 0]}
                                                                    name={color.charAt(0) + color.slice(1).toLowerCase()}
                                                                    stroke={inkBarColors[color]}
                                                                    strokeWidth={1}
                                                                    xAxisId={0}
                                                                />
                                                            ))}
                                                        </>
                                                    ) : (
                                                        // Słupki dla rzadkości
                                                        <>
                                                            <Bar
                                                                dataKey="COMMON"
                                                                fill={rarityBarGradients.COMMON}
                                                                radius={[0, 4, 4, 0]}
                                                                name="Common"
                                                                stroke="#4b5563"
                                                                strokeWidth={1}
                                                                xAxisId={0}
                                                            />
                                                            <Bar
                                                                dataKey="UNCOMMON"
                                                                fill={rarityBarGradients.UNCOMMON}
                                                                radius={[0, 4, 4, 0]}
                                                                name="Uncommon"
                                                                stroke="#e5e5e5"
                                                                strokeWidth={1}
                                                                xAxisId={0}
                                                            />
                                                            <Bar
                                                                dataKey="RARE"
                                                                fill={rarityBarGradients.RARE}
                                                                radius={[0, 4, 4, 0]}
                                                                name="Rare"
                                                                stroke="#b8860b"
                                                                strokeWidth={1}
                                                                xAxisId={0}
                                                            />
                                                            <Bar
                                                                dataKey="SUPER"
                                                                fill={rarityBarGradients.SUPER}
                                                                radius={[0, 4, 4, 0]}
                                                                name="Super"
                                                                stroke="#e5e5e5"
                                                                strokeWidth={1}
                                                                xAxisId={0}
                                                            />
                                                            <Bar
                                                                dataKey="LEGENDARY"
                                                                fill={rarityBarGradients.LEGENDARY}
                                                                radius={[0, 4, 4, 0]}
                                                                name="Legendary"
                                                                stroke="#daa520"
                                                                strokeWidth={1}
                                                                xAxisId={0}
                                                            />
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        // Wykres poziomy dla desktopów
                                        <>
                                            <XAxis
                                                type="category"
                                                dataKey={activeTab === 'colors' ? "color" : "cost"}
                                                stroke="rgba(255,255,255,0.7)"
                                                fontSize={14}
                                            />
                                            <YAxis
                                                type="number"
                                                stroke="rgba(255,255,255,0.7)"
                                                fontSize={14}
                                                yAxisId={0}
                                            />
                                            <YAxis
                                                type="number"
                                                orientation="right"
                                                stroke="#ff6b6b"
                                                fontSize={12}
                                                tick={{ fill: "#ff6b6b" }}
                                                yAxisId={1}
                                                domain={[0, 'dataMax']}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="total"
                                                stroke="#ff6b6b"
                                                strokeWidth={3}
                                                dot={{ fill: "#ff6b6b", strokeWidth: 2, r: 4 }}
                                                name="Suma"
                                                connectNulls={false}
                                                yAxisId={1}
                                            />
                                            {activeTab === 'colors' ? (
                                                // Słupki dla rzadkości (tab "Podział na kolory")
                                                <>
                                                    <Bar
                                                        dataKey="COMMON"
                                                        fill={rarityBarGradients.COMMON}
                                                        radius={[4, 4, 0, 0]}
                                                        name="Common"
                                                        stroke="#4b5563"
                                                        strokeWidth={1}
                                                        yAxisId={0}
                                                    />
                                                    <Bar
                                                        dataKey="UNCOMMON"
                                                        fill={rarityBarGradients.UNCOMMON}
                                                        radius={[4, 4, 0, 0]}
                                                        name="Uncommon"
                                                        stroke="#e5e5e5"
                                                        strokeWidth={1}
                                                        yAxisId={0}
                                                    />
                                                    <Bar
                                                        dataKey="RARE"
                                                        fill={rarityBarGradients.RARE}
                                                        radius={[4, 4, 0, 0]}
                                                        name="Rare"
                                                        stroke="#b8860b"
                                                        strokeWidth={1}
                                                        yAxisId={0}
                                                    />
                                                    <Bar
                                                        dataKey="SUPER"
                                                        fill={rarityBarGradients.SUPER}
                                                        radius={[4, 4, 0, 0]}
                                                        name="Super"
                                                        stroke="#e5e5e5"
                                                        strokeWidth={1}
                                                        yAxisId={0}
                                                    />
                                                    <Bar
                                                        dataKey="LEGENDARY"
                                                        fill={rarityBarGradients.LEGENDARY}
                                                        radius={[4, 4, 0, 0]}
                                                        name="Legendary"
                                                        stroke="#daa520"
                                                        strokeWidth={1}
                                                        yAxisId={0}
                                                    />
                                                </>
                                            ) : (
                                                // Słupki dla taba "Per koszt"
                                                <>
                                                    {costView === 'colors' ? (
                                                        // Słupki dla kolorów
                                                        <>
                                                            {Object.entries(visibleColors).map(([color, isVisible]) => isVisible && (
                                                                <Bar
                                                                    key={color}
                                                                    dataKey={color}
                                                                    fill={inkBarColors[color]}
                                                                    radius={[4, 4, 0, 0]}
                                                                    name={color.charAt(0) + color.slice(1).toLowerCase()}
                                                                    stroke={inkBarColors[color]}
                                                                    strokeWidth={1}
                                                                    yAxisId={0}
                                                                />
                                                            ))}
                                                        </>
                                                    ) : (
                                                        // Słupki dla rzadkości
                                                        <>
                                                            <Bar
                                                                dataKey="COMMON"
                                                                fill={rarityBarGradients.COMMON}
                                                                radius={[4, 4, 0, 0]}
                                                                name="Common"
                                                                stroke="#4b5563"
                                                                strokeWidth={1}
                                                                yAxisId={0}
                                                            />
                                                            <Bar
                                                                dataKey="UNCOMMON"
                                                                fill={rarityBarGradients.UNCOMMON}
                                                                radius={[4, 4, 0, 0]}
                                                                name="Uncommon"
                                                                stroke="#e5e5e5"
                                                                strokeWidth={1}
                                                                yAxisId={0}
                                                            />
                                                            <Bar
                                                                dataKey="RARE"
                                                                fill={rarityBarGradients.RARE}
                                                                radius={[4, 4, 0, 0]}
                                                                name="Rare"
                                                                stroke="#b8860b"
                                                                strokeWidth={1}
                                                                yAxisId={0}
                                                            />
                                                            <Bar
                                                                dataKey="SUPER"
                                                                fill={rarityBarGradients.SUPER}
                                                                radius={[4, 4, 0, 0]}
                                                                name="Super"
                                                                stroke="#e5e5e5"
                                                                strokeWidth={1}
                                                                yAxisId={0}
                                                            />
                                                            <Bar
                                                                dataKey="LEGENDARY"
                                                                fill={rarityBarGradients.LEGENDARY}
                                                                radius={[4, 4, 0, 0]}
                                                                name="Legendary"
                                                                stroke="#daa520"
                                                                strokeWidth={1}
                                                                yAxisId={0}
                                                            />
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}

                                    {!isMobile && (
                                        <Legend
                                            wrapperStyle={{
                                                color: 'white',
                                                fontSize: '12px'
                                            }}
                                        />
                                    )}
                                </ComposedChart>
                            </ResponsiveContainer>
                        </Box>

                        {/* Opis wykresu na dole */}
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'rgba(255, 255, 255, 0.7)',
                                textAlign: 'center',
                                mt: { xs: 1, md: 2 },
                                fontSize: { xs: '0.75rem', md: '0.875rem' },
                                fontStyle: 'italic',
                                px: 1,
                                flexShrink: 0
                            }}
                        >
                            {getChartDescription()}
                        </Typography>
                    </Box>
                </Paper >
            </Modal >
        </>
    );
};

export default LimitedTierlistStats; 